import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, 
  Wrench, 
  CheckCircle, 
  Clock, 
  FileText, 
  List, 
  Users,
  Settings,
  BarChart3,
  XCircle
} from 'lucide-react';
import { ServiceAdvisorService } from '../../services/serviceAdvisorService';
import { RealtimeService } from '../../services/realtimeService';
import MyFNGComplaintRegistration from '../../components/service_advisor/MyFNGComplaintRegistration';
import MyFNGComplaintsList from '../../components/service_advisor/MyFNGComplaintsList';

const ServiceAdvisorDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  
  const [showMyFNGModal, setShowMyFNGModal] = useState(false);
  const [showMyFNGList, setShowMyFNGList] = useState(false);
  const [lastUpdate, setLastUpdate] = useState('');

  // Fetch statistics from RPC (like mobile app)
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['serviceAdvisorStatistics', user?.id],
    queryFn: () => ServiceAdvisorService.getAdvisorStatistics(user?.id),
    enabled: !!user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch complaints for real-time updates - use getComplaintsFull (same as mobile app)
  const { data: complaintsData } = useQuery({
    queryKey: ['serviceAdvisorComplaints', user?.id],
    queryFn: () => ServiceAdvisorService.getComplaintsFull(user?.id, '', true),
    enabled: !!user?.id,
    select: (result) => result.success ? result.data : [],
  });

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to service requests changes
    const serviceRequestsChannel = RealtimeService.subscribeToServiceRequests((payload) => {
      console.log('Service request updated:', payload);
      // Invalidate queries to refetch data
      queryClient.invalidateQueries(['serviceAdvisorComplaints', user.id]);
      queryClient.invalidateQueries(['serviceAdvisorStatistics', user.id]);
      setLastUpdate(new Date().toLocaleTimeString());
    });

    // Subscribe to MyFNG complaints changes
    const myfngChannel = RealtimeService.subscribeToMyFNGComplaints(user.id, (payload) => {
      console.log('MyFNG complaint updated:', payload);
      queryClient.invalidateQueries(['myfngComplaints', user.id]);
    });

    // Update last update time
    setLastUpdate(new Date().toLocaleTimeString());

    // Cleanup on unmount
    return () => {
      RealtimeService.unsubscribe('service_requests');
      RealtimeService.unsubscribe(`myfng_complaints_${user.id}`);
    };
  }, [user?.id, queryClient]);

  // Extract statistics (handle both formats)
  const statistics = statsData?.data || {};
  const stats = {
    totalRequests: statistics.totalrequests || statistics.totalRequests || statistics.total_complaints || 0,
    pendingRequests: statistics.pendingrequests || statistics.pendingRequests || 0,
    completedRequests: statistics.completedrequests || statistics.completedRequests || 0,
    cancelledRequests: statistics.cancelledrequests || statistics.cancelledRequests || 0,
  };

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Service Advisor Dashboard</h1>
          <p className="text-white/80">Welcome back, {user?.name || 'Service Advisor'}</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Service Advisor Dashboard</h1>
            <p className="text-white/80">Welcome back, {user?.name || 'Service Advisor'}</p>
          </div>
          <div className="text-right">
            {lastUpdate && (
              <p className="text-sm text-white/80 flex items-center gap-1">
                <Clock size={14} />
                Last updated: {lastUpdate}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Performance Overview Stats */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Performance Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Requests</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalRequests}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Wrench size={24} className="text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending</p>
                <p className="text-3xl font-bold text-gray-900">{stats.pendingRequests}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock size={24} className="text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Completed</p>
                <p className="text-3xl font-bold text-gray-900">{stats.completedRequests}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle size={24} className="text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Cancelled</p>
                <p className="text-3xl font-bold text-gray-900">{stats.cancelledRequests}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle size={24} className="text-red-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/service_advisor/complaint-registration')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            <AlertCircle size={20} />
            Register Complaint
          </button>
          
          <button 
            onClick={() => setShowMyFNGModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            <FileText size={20} />
            Register MyFNG Complaint
          </button>
          
          <button 
            onClick={() => setShowMyFNGList(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <List size={20} />
            My MyFNG Complaints
          </button>
          
          <button 
            onClick={() => navigate('/service_advisor/complaints')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
          >
            <AlertCircle size={20} />
            View All Complaints
          </button>
          
          <button 
            onClick={() => navigate('/service_advisor/registered')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <FileText size={20} />
            View Registered
          </button>
          
          <button 
            onClick={() => navigate('/service_advisor/mechanics')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
          >
            <Wrench size={20} />
            Manage Mechanics
          </button>
          
          <button 
            onClick={() => navigate('/service_advisor/membership-customer')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
          >
            <Users size={20} />
            Membership Customer
          </button>
          
          <button 
            onClick={() => {/* TODO: Navigate to reports */}}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
          >
            <BarChart3 size={20} />
            Reports
          </button>
          
          <button 
            onClick={() => {/* TODO: Navigate to settings */}}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <Settings size={20} />
            Settings
          </button>
        </div>
      </div>

      {/* MyFNG Complaint Registration Modal */}
      {showMyFNGModal && (
        <MyFNGComplaintRegistration
          visible={showMyFNGModal}
          onClose={() => setShowMyFNGModal(false)}
          userId={user?.id}
        />
      )}

      {/* MyFNG Complaints List Modal */}
      {showMyFNGList && (
        <MyFNGComplaintsList
          visible={showMyFNGList}
          onClose={() => setShowMyFNGList(false)}
          userId={user?.id}
        />
      )}

      {/* Top Customers */}
      {complaintsData && complaintsData.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Users size={24} />
            <span>Top Customers</span>
          </h2>
          <div className="space-y-3">
            {[...new Set(complaintsData.map(c => c.customer_name).filter(Boolean))]
              .slice(0, 5)
              .map((customerName, index) => {
                const customerComplaints = complaintsData.filter(c => c.customer_name === customerName);
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{customerName}</p>
                      <p className="text-sm text-gray-600">
                        {customerComplaints.length} complaint{customerComplaints.length > 1 ? 's' : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate('/service_advisor/complaints', {
                        state: { searchQuery: customerName }
                      })}
                      className="px-3 py-1 text-sm text-teal-600 hover:text-teal-700 hover:underline"
                    >
                      View
                    </button>
                  </div>
                );
              })}
            {[...new Set(complaintsData.map(c => c.customer_name).filter(Boolean))].length === 0 && (
              <p className="text-gray-500 text-center py-4">No customer data available</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceAdvisorDashboard;
